// src/router/index.ts
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import store from '@/store';

const Login = () => import('@/views/Login.vue');
const Dashboard = () => import('@/views/Dashboard.vue');
const Profile = () => import('@/views/Profile.vue');
const Password = () => import('@/views/Password.vue');
const WinLoseMember = () => import('@/views/WinLoseMember.vue');
const ProviderReport = () => import('@/views/ProviderReport.vue');
const GameReport = () => import('@/views/GameReport.vue');
const HighValueReport = () => import('@/views/HighValueReport.vue');
const UserOnline = () => import('@/views/UserOnline.vue');
const Downline = () => import('@/views/Downline.vue');
const SubAccount = () => import('@/views/SubAccount.vue');
const DepositWithdraw = () => import('@/views/DepositWithdraw.vue');
const SuggestMember = () => import('@/views/SuggestMember.vue');
const Message = () => import('@/views/Message.vue');
const Banner = () => import('@/views/Banner.vue');
const BetCancel = () => import('@/views/BetCancel.vue');
const Payout = () => import('@/views/Payout.vue');
const Recal = () => import('@/views/Recal.vue');
const AddSubAccount = () => import('@/views/AddSubAccount.vue');
const AddAgent = () => import('@/views/AddAgent.vue');
const EditAgent = () => import('@/views/EditAgent.vue');
const AddMember = () => import('@/views/AddMember.vue');
const EditMember = () => import('@/views/EditMember.vue');
const EditSubAccount = () => import('@/views/EditSubAccount.vue');
const SeamlessKey = () => import('@/views/SeamlessKey.vue');
const TransferKey = () => import('@/views/TransferKey.vue');
const Social = () => import('@/views/Social.vue');
const Promotion = () => import('@/views/Promotion.vue');
const MechanicReport = () => import('@/views/MechanicReport.vue');

// ===== ADMIN =====
const GameMessage = () => import('@/views/GameMessage.vue');
const GameVersion = () => import('@/views/GameVersion.vue');
const GameVersionControl = () => import('@/views/GameVersionControl.vue');
const SecureCode = () => import('@/views/SecureCode.vue');
const Bankaccount = () => import('@/views/BankAccount.vue');
const Invoice = () => import('@/views/Invoice.vue');
const DocumentApi = () => import('@/views/DocumentApi.vue');
const RateCurrency = () => import('@/views/RateCurrency.vue');
const ResetPasswordLog = () => import('@/views/ResetPasswordLog.vue');
const SecretPassword = () => import('@/views/SecretPassword.vue');
const ActiveAgent = () => import('@/views/ActivityAgent.vue');
const Point = () => import('@/views/Point.vue');
const ActivityLog = () => import('@/views/Activity.vue');
const Feature = () => import('@/views/FeatureView.vue');
const UserSeamlessProblem = () => import('@/views/UserSeamlessProblem.vue');
const PaymentLog = () => import('@/views/PaymentLog.vue');
const UpdateCommissionLog = () => import('@/views/UpdateCommissionLog.vue');
const DemoUrl = () => import('@/views/DemoUrl.vue');
const LegendOfDolly = () => import('@/views/LegendOfDolly.vue');
const GameAnalytics = () => import('@/views/GameAnalytics.vue');
const LogAdmin = () => import('@/views/LogAdmin.vue');
const User = () => import('@/views/User.vue');
const Theme = () => import('@/views/Theme.vue');
const Lobby = () => import('@/views/Lobby.vue');
const agentSetting = () => import('@/views/AgentSetting.vue');
const landingPageSetting = () => import('@/views/LandingPageSetting.vue');
const themeSelection = () => import('@/views/ThemeSelection.vue');
const rtpManagement = () => import('@/views/RtpManagement.vue');
const rtpAgentManagement = () => import('@/views/RtpAgentManagement.vue');
const riskManagement = () => import('@/views/RiskManagement.vue');
const Mascot = () => import('@/views/Mascot.vue');
const Angpow = () => import('@/views/Angpow.vue');
const Coin = () => import('@/views/Coin.vue');
const Url = () => import('@/views/AddUrl.vue');
const WinloseMechanic = () => import('@/views/WinloseMechanic.vue');
const PatternVersion = () => import('@/views/PatternVersion.vue');
const DomainPool = () => import('@/views/DomainPool.vue');
const Campaign = () => import('@/views/Campaign.vue');
const AutoLogin = () => import('@/views/AutoLogin.vue');

type Role = 'COMPANY' | 'SUBCOMPANY' | 'SUPERADMIN' | 'AGENT' | 'SUBAGENT' | 'ADMIN' | 'SUBACCOUNT';

interface MetaEx {
  requiresAuth?: boolean;
  guest?: boolean;
  allow?: Role[];
  permKey?: string;
  isEditPermKey?: string;
  requiresWhiteLabel?: boolean;
}

const routes: RouteRecordRaw[] = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'Dashboard' }
  },
  {
    path: '/auto-login',
    name: 'AutoLogin',
    component: AutoLogin,
    meta: {
      guest: true,
      layout: 'auth'
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { guest: true, layout: 'auth' }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: {
      requiresAuth: true,
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'AGENT', 'SUBAGENT', 'ADMIN']
    }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: Profile,
    meta: {
      requiresAuth: true,
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'AGENT', 'SUBAGENT', 'ADMIN']
    }
  },
  {
    path: '/password',
    name: 'Password',
    component: Password,
    meta: {
      requiresAuth: true,
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'AGENT', 'SUBAGENT', 'ADMIN']
    }
  },
  {
    path: '/winlosemember',
    name: 'WinLoseMember',
    component: WinLoseMember,
    meta: {
      requiresAuth: true,
      permKey: 'report',
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'AGENT', 'SUBAGENT', 'ADMIN']
    }
  },
  {
    path: '/winlosemechanic',
    name: 'WinLoseMechanic',
    component: WinloseMechanic,
    meta: {
      requiresAuth: true,
      permKey: 'reportMechanic',
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'AGENT', 'SUBAGENT', 'ADMIN']
    }
  },
  {
    path: '/provider-report',
    name: 'ProviderReport',
    component: ProviderReport,
    meta: {
      requiresAuth: true,
      permKey: 'report',
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN']
    }
  },
  {
    path: '/game-report',
    name: 'GameReport',
    component: GameReport,
    meta: {
      requiresAuth: true,
      permKey: 'report',
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'AGENT']
    }
  },
  {
    path: '/high-value-report',
    name: 'HighValueReport',
    component: HighValueReport,
    meta: {
      requiresAuth: true,
      permKey: 'report',
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'ADMIN']
    }
  },
  {
    path: '/mechanic-report',
    name: 'MechanicReport',
    component: MechanicReport,
    meta: {
      requiresAuth: true,
      permKey: 'reportMechanic',
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN']
    }
  },
  {
    path: '/deposit-withdraw',
    name: 'DepositWithdraw',
    component: DepositWithdraw,
    meta: {
      requiresAuth: true,
      permKey: 'statement',
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'AGENT', 'SUBAGENT', 'ADMIN']
    }
  },
  {
    path: '/useronline',
    name: 'UserOnline',
    component: UserOnline,
    meta: {
      requiresAuth: true,
      permKey: 'useronline',
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'ADMIN', 'AGENT', 'SUBAGENT']
    }
  },
  {
    path: '/downline',
    name: 'Downline',
    component: Downline,
    meta: {
      requiresAuth: true,
      permKey: 'member',
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'AGENT', 'SUBAGENT', 'ADMIN']
    }
  },
  {
    path: '/subaccount',
    name: 'SubAccount',
    component: SubAccount,
    meta: { requiresAuth: true, permKey: 'member', allow: ['COMPANY', 'AGENT'] }
  },
  {
    path: '/add-subaccount',
    name: 'AddSubAccount',
    component: AddSubAccount,
    meta: { requiresAuth: true, permKey: 'member', allow: ['COMPANY', 'AGENT'] }
  },
  {
    path: '/edit-subaccount/:id',
    name: 'EditSubAccount',
    component: EditSubAccount,
    meta: {
      requiresAuth: true,
      permKey: 'member',
      allow: ['COMPANY', 'AGENT'],
      isEditPermKey: 'member'
    }
  },
  {
    path: '/add-agent',
    name: 'AddAgent',
    component: AddAgent,
    meta: {
      requiresAuth: true,
      permKey: 'member',
      allow: ['COMPANY', 'SUBCOMPANY', 'AGENT'],
      isEditPermKey: 'member'
    }
  },
  {
    path: '/edit-agent/:id',
    name: 'EditAgent',
    component: EditAgent,
    meta: {
      requiresAuth: true,
      permKey: 'member',
      allow: ['COMPANY', 'SUBCOMPANY', 'SUBAGENT', 'ADMIN', 'AGENT'],
      isEditPermKey: 'member'
    }
  },
  {
    path: '/add-member',
    name: 'AddMember',
    component: AddMember,
    meta: {
      requiresAuth: true,
      permKey: 'member',
      allow: ['AGENT'],
      isEditPermKey: 'member'
    }
  },
  {
    path: '/edit-member/:id',
    name: 'EditMember',
    component: EditMember,
    meta: {
      requiresAuth: true,
      permKey: 'member',
      allow: ['COMPANY', 'SUBCOMPANY', 'AGENT', 'SUBAGENT'],
      isEditPermKey: 'member'
    }
  },
  {
    path: '/suggest-member',
    name: 'SuggestMember',
    component: SuggestMember,
    meta: {
      requiresAuth: true,
      permKey: 'suggestion',
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'ADMIN', 'AGENT']
    }
  },
  {
    path: '/seamless-key',
    name: 'SeamlessKey',
    component: SeamlessKey,
    meta: {
      requiresAuth: true,
      permKey: 'apikey',
      allow: ['SUPERADMIN', 'AGENT', 'SUBAGENT', 'ADMIN']
    }
  },
  {
    path: '/transfer-key',
    name: 'TransferKey',
    component: TransferKey,
    meta: {
      requiresAuth: true,
      permKey: 'apikey',
      allow: ['SUPERADMIN', 'AGENT', 'SUBAGENT', 'ADMIN']
    }
  },
  {
    path: '/social',
    name: 'Social',
    component: Social,
    meta: { requiresAuth: true, permKey: 'social', allow: ['AGENT', 'SUBAGENT'] }
  },
  {
    path: '/promotion',
    name: 'Promotion',
    component: Promotion,
    meta: { requiresAuth: true, allow: ['AGENT', 'SUBAGENT'] }
  },
  {
    path: '/game-version',
    name: 'GameVersion',
    component: GameVersion,
    meta: { requiresAuth: true, permKey: 'admin', allow: ['SUPERADMIN', 'AGENT', 'ADMIN'] }
  },
  {
    path: '/pattern-version',
    name: 'Pattern',
    component: PatternVersion,
    meta: { requiresAuth: true, permKey: 'admin', allow: ['SUPERADMIN', 'AGENT', 'ADMIN'] }
  },
  {
    path: '/game-version-control',
    name: 'GameVersionControl',
    component: GameVersionControl,
    meta: { requiresAuth: true, permKey: 'admin', allow: ['SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/game-message',
    name: 'GameMessage',
    component: GameMessage,
    meta: { requiresAuth: true, permKey: 'admin', allow: ['SUPERADMIN', 'AGENT'] }
  },
  {
    path: '/demo-url',
    name: 'DemoUrl',
    component: DemoUrl,
    meta: { requiresAuth: true, permKey: 'admin', allow: ['SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/secure',
    name: 'SecureCode',
    component: SecureCode,
    meta: { requiresAuth: true, permKey: 'admin', allow: ['SUPERADMIN', 'AGENT'] }
  },
  {
    path: '/message',
    name: 'Message',
    component: Message,
    meta: { requiresAuth: true, allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/banner',
    name: 'Banner',
    component: Banner,
    meta: { requiresAuth: true, allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/bet-cancel',
    name: 'BetCancel',
    component: BetCancel,
    meta: {
      requiresAuth: true,
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'AGENT', 'SUBAGENT']
    }
  },
  {
    path: '/payout',
    name: 'Payout',
    component: Payout,
    meta: {
      requiresAuth: true,
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'AGENT', 'SUBAGENT']
    }
  },
  {
    path: '/recal',
    name: 'Recal',
    component: Recal,
    meta: {
      requiresAuth: true,
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'AGENT', 'SUBAGENT']
    }
  },
  {
    path: '/bank-account',
    name: 'Bankaccount',
    component: Bankaccount,
    meta: { requiresAuth: true, allow: ['SUPERADMIN', 'SUBCOMPANY'] }
  },
  {
    path: '/invoice',
    name: 'Invoice',
    component: Invoice,
    meta: { requiresAuth: true, allow: ['SUPERADMIN', 'SUBCOMPANY'] }
  },
  {
    path: '/documentapi',
    name: 'DocumentApi',
    component: DocumentApi,
    meta: { requiresAuth: true, allow: ['AGENT', 'ADMIN', 'SUPERADMIN', 'SUBAGENT'] }
  },
  {
    path: '/ratecurrency',
    name: 'RateCurrency',
    component: RateCurrency,
    meta: {
      requiresAuth: true,
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'ADMIN', 'AGENT', 'SUBAGENT']
    }
  },
  {
    path: '/reset-passlog',
    name: 'Reset Password Log',
    component: ResetPasswordLog,
    meta: { requiresAuth: true, permKey: 'admin', allow: ['SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/update-commission-log',
    name: 'Update Commission Log',
    component: UpdateCommissionLog,
    meta: { requiresAuth: true, permKey: 'admin', allow: ['SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/secret-password',
    name: 'SecretPassword',
    component: SecretPassword,
    meta: { requiresAuth: true, permKey: 'admin', allow: ['SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/access-logs',
    name: 'AccessLogs',
    component: ActiveAgent,
    meta: {
      requiresAuth: true,
      allow: ['COMPANY', 'SUBCOMPANY', 'AGENT', 'SUBACCOUNT', 'SUBAGENT']
    }
  },
  {
    path: '/point',
    name: 'Point',
    component: Point,
    meta: {
      requiresAuth: true,
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'ADMIN', 'AGENT', 'SUBACCOUNT', 'SUBAGENT']
    }
  },
  {
    path: '/coin',
    name: 'Coin',
    component: Coin,
    meta: {
      requiresAuth: true,
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'ADMIN', 'AGENT', 'SUBACCOUNT', 'SUBAGENT']
    }
  },
  {
    path: '/url',
    name: 'Url',
    component: Url,
    meta: {
      requiresAuth: true,
      allow: ['COMPANY', 'SUBCOMPANY', 'SUPERADMIN', 'ADMIN', 'AGENT', 'SUBACCOUNT', 'SUBAGENT']
    }
  },
  {
    path: '/legend-of-dolly',
    name: 'Legend of dolly',
    component: LegendOfDolly,
    meta: { requiresAuth: true, allow: ['SUBCOMPANY', 'SUPERADMIN'] }
  },
  {
    path: '/game-analytics',
    name: 'Game Analytics',
    component: GameAnalytics,
    meta: { requiresAuth: true, allow: ['SUBCOMPANY', 'SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/activity',
    name: 'ActivityLog',
    component: ActivityLog,
    meta: { requiresAuth: true, allow: ['SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/feature',
    name: 'Feature',
    component: Feature,
    meta: { requiresAuth: true, allow: ['COMPANY', 'SUBCOMPANY', 'AGENT', 'SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/mascot',
    name: 'Mascot',
    component: Mascot,
    meta: { requiresAuth: true, allow: ['SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/angpow',
    name: 'Angpow',
    component: Angpow,
    meta: { requiresAuth: true, allow: ['SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/user-seamless-problem',
    name: 'User Seamless',
    component: UserSeamlessProblem,
    meta: { requiresAuth: true, allow: ['SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/paymentlog',
    name: 'PaymentLog',
    component: PaymentLog,
    meta: {
      requiresAuth: true,
      allow: ['COMPANY', 'SUBCOMPANY', 'AGENT', 'SUBACCOUNT', 'SUBAGENT']
    }
  },
  {
    path: '/log-admin',
    name: 'LogAdmin',
    component: LogAdmin,
    meta: { requiresAuth: true, allow: ['SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/user',
    name: 'User',
    component: User,
    meta: { requiresAuth: true, allow: ['SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/backoffice',
    name: 'BO',
    component: Theme,
    meta: { requiresAuth: true, allow: ['SUPERADMIN', 'COMPANY', 'SUBCOMPANY'] }
  },
  {
    path: '/lobby',
    name: 'LB',
    component: Lobby,
    meta: { requiresAuth: true, allow: ['SUPERADMIN', 'COMPANY', 'SUBCOMPANY'] }
  },
  {
    path: '/agentsetting',
    name: 'BO-agent',
    component: agentSetting,
    meta: { requiresAuth: true, allow: ['AGENT'] }
  },
  {
    path: '/landingpagesetting',
    name: 'BO-landing',
    component: landingPageSetting,
    meta: { requiresAuth: true, allow: ['AGENT'] }
  },
  {
    path: '/themeselection',
    name: 'BO-theme',
    component: themeSelection,
    meta: { requiresAuth: true, requiresWhiteLabel: true, allow: ['AGENT'] }
  },
  {
    path: '/rtp-management',
    name: 'RTPM',
    component: rtpManagement,
    meta: { requiresAuth: true, allow: ['SUPERADMIN'] }
  },
  {
    path: '/rtp-agent-management',
    name: 'RTPAM',
    component: rtpAgentManagement,
    meta: { requiresAuth: true, allow: ['SUPERADMIN'] }
  },
  {
    path: '/rtp-riskmanagement',
    name: 'RM',
    component: riskManagement,
    meta: { requiresAuth: true, allow: ['SUPERADMIN'] }
  },
  {
    path: '/domain-pool',
    name: 'DomainPool',
    component: DomainPool,
    meta: { requiresAuth: true, allow: ['SUPERADMIN', 'ADMIN'] }
  },
  {
    path: '/campaign',
    name: 'Campaign',
    component: Campaign,
    meta: { requiresAuth: true, allow: ['SUPERADMIN'] }
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior: () => ({ left: 0, top: 0 })
});

router.beforeEach(to => {
  if (to.path === '/auto-login') {
    return true;
  }

  const { isAuthenticated, userPermission, role } = store.getters;

  const requiresAuth = to.matched.some(r => (r.meta as MetaEx).requiresAuth);
  const guest = to.matched.some(r => (r.meta as MetaEx).guest);

  if (requiresAuth) {
    if (!isAuthenticated) return '/login';

    const meta = (to.meta || {}) as MetaEx;
    const { permKey } = meta;
    const { allow } = meta;
    const { isEditPermKey } = meta;

    const permOk = !permKey || userPermission?.[permKey] !== 'off';
    const allowOk = !allow || allow.includes(role);
    const editOk = !isEditPermKey || userPermission?.[isEditPermKey] === 'edit';

    if (permOk && allowOk && editOk) return true;
    return '/dashboard';
  }

  if (guest) {
    if (!isAuthenticated) return true;
    return '/dashboard';
  }

  return true;
});

export default router;
